import * as React from "react";
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";

import { useAuth } from "@/providers";
import spec from "@/shared/components/invocationSpec/invocationSpec.module.css";
import { ParameterRow } from "@/shared/components/invocationSpec/ParameterRow";
import {
  requiredWithoutDefault,
  type AgentRecord,
} from "@/shared/domain/agents";
import { useAgents } from "@/shared/domain/agents/hooks";
import { Combobox, Field, FormBlock } from "@/shared/foundation/primitives";

import { type TriggerFormValues } from "../../triggerFormSchema";
import { useFormTemplateCatalog } from "../../useFormTemplateCatalog";
import styles from "../targets.module.css";

import { prefillAgentValues } from "./agentVariablePrefill";

export interface StartAgentTargetCardProps {
  index: number;
}

/**
 * Which agent to launch, and what to hand it.
 *
 * Choosing an agent seeds the variables the checked changes can answer, so an
 * agent expecting a dataset starts pointed at the one that changed rather than
 * at nothing.
 */
export function StartAgentTargetCard({ index }: StartAgentTargetCardProps) {
  const { control, setValue, getValues, getFieldState } =
    useFormContext<TriggerFormValues>();
  const formState = useFormState({ control });
  const { currentOrganization } = useAuth();
  const catalog = useFormTemplateCatalog();
  const agentTitleId = React.useId();
  const variablesTitleId = React.useId();
  const agentsQuery = useAgents({ orgId: currentOrganization?.org_id });
  const agents = agentsQuery.data ?? [];

  const agentId = useWatch<TriggerFormValues>({
    control,
    name: `targets.${index}.agentId`,
  });
  const chosen = agents.find((agent) => agent.agent_id === agentId) ?? null;

  // Note the variables the agent cannot start without, once it is known —
  // just picked, or loaded for a stored trigger — so validation can insist
  // on them. Written only on a change, so loading does not dirty the form.
  React.useEffect(() => {
    const next =
      chosen === null ? [] : requiredWithoutDefault(chosen.variables);
    const current = getValues(`targets.${index}.requiredVariables`) ?? [];
    if (current.join("\u0000") !== next.join("\u0000")) {
      setValue(`targets.${index}.requiredVariables`, next);
    }
  }, [chosen, index, getValues, setValue]);

  return (
    <div className={styles.body} data-testid={`trigger-target-${index}-agent`}>
      <FormBlock
        titleId={agentTitleId}
        title="Agent"
        hint="The agent to start when this trigger fires."
      >
        <Controller
          control={control}
          name={`targets.${index}.agentId`}
          render={({ field, fieldState }) => (
            <Field.Root invalid={fieldState.invalid}>
              <Combobox<AgentRecord>
                items={agents}
                value={chosen}
                onValueChange={(agent) => {
                  field.onChange(agent === null ? null : agent.agent_id);
                  setValue(
                    `targets.${index}.variables`,
                    agent === null
                      ? {}
                      : prefillAgentValues(agent.variables, catalog),
                  );
                }}
                itemToStringLabel={(agent) => agent.name}
                isItemEqualToValue={(item, value) =>
                  item.agent_id === value.agent_id
                }
                autoHighlight
                size="lg"
              >
                <Combobox.TriggerInput
                  placeholder="Search agents"
                  aria-labelledby={agentTitleId}
                  ref={field.ref}
                />
                <Combobox.Content>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(agent: AgentRecord) => (
                      <Combobox.Item key={agent.agent_id} value={agent} />
                    )}
                  </Combobox.List>
                </Combobox.Content>
              </Combobox>
              <Field.Error match>{fieldState.error?.message}</Field.Error>
            </Field.Root>
          )}
        />
      </FormBlock>

      {chosen !== null && chosen.variables.length > 0 && (
        <FormBlock
          titleId={variablesTitleId}
          title="Agent variables"
          hint="A variable left alone follows the agent’s own default when the thread starts."
          data-testid={`trigger-target-${index}-agent-variables`}
          data-count={chosen.variables.length}
        >
          <div className={spec.group}>
            {chosen.variables.map((variable) => (
              <ParameterRow
                key={variable.name}
                name={`targets.${index}.variables`}
                parameter={variable}
                templateCatalog={catalog}
                error={
                  getFieldState(
                    `targets.${index}.variables.${variable.name}`,
                    formState,
                  ).error?.message
                }
              />
            ))}
          </div>
        </FormBlock>
      )}
    </div>
  );
}
